import { useEffect, useMemo, useState } from 'react';
import { useSnackbar } from 'notistack';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import { Box, Divider, Typography, useMediaQuery } from '@mui/material';
import { FileDownload } from '@mui/icons-material';
import {
  OrderManagementPage,
  PageTitle,
  PageTitleWrapper,
  Pagination,
  Table
} from '../../../components';
import { ISiteReport } from '../../../models';
import { getSitePlanningReport, getSitePrepReport, getSiteReports } from '../../../fetch';
import { SiteReportsFilters } from './site-reports-filters';
import { MobileSiteReports } from './mobile-site-reports';
import { LoadingButton } from '@mui/lab';
import { downloadFile } from '../../../helpers';

export const SiteReportsList = () => {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();

  const isMobile = useMediaQuery('(max-width: 1023px)');

  const [isLoadingSiteReports, setIsLoadingSiteReports] = useState(false);
  const [siteReports, setSiteReports] = useState<ISiteReport[]>([]);

  const [page, setPage] = useState<number>(0);
  const [recordCount, setRecordCount] = useState<number>(0);
  const [perPage, setRowsPerPage] = useState<number>(10);

  const [hasAppliedFilters, setHasAppliedFilters] = useState(false);
  const [filters, setFilters] = useState<{
    jobStatus?: string | null;
    jobType?: string | null;
  }>({});

  const [selectedNeighborhood, setSelectedNeighborhood] = useState<string>('');
  const [selectedStatus, setSelectedStatus] = useState<string>('');
  const [selectedType, setSelectedType] = useState<string>('');

  const fetchSiteReports = async () => {
    try {
      setIsLoadingSiteReports(true);
      const res = await getSiteReports(selectedNeighborhood, {
        page: page + 1,
        perPage,
        sortBy: 'JobId',
        sortDirection: 'Asc',
        ...filters
      });
      setSiteReports(res.records);
      setRecordCount(res.totalRecordCount);
    } catch (error) {
      enqueueSnackbar(error?.Detail || `Error loading site reports, please try again.`, {
        variant: 'error'
      });
    } finally {
      setIsLoadingSiteReports(false);
    }
  };

  const [isDownloadingSitePrepReport, setIsDownloadingSitePrepReport] = useState<{
    [id: string | number]: boolean;
  }>({});
  const fetchSitePrepReport = async (report: ISiteReport) => {
    try {
      setIsDownloadingSitePrepReport({
        [report.jobId]: true
      });
      const res = await getSitePrepReport(report.jobId);
      if (res && res.type.includes('application/pdf')) {
        return downloadFile(res, `Site Prep Report - ${report.code}`);
      }
    } catch (error) {
      enqueueSnackbar(error?.Detail || `Error downloading site prep reports, please try again.`, {
        variant: 'error'
      });
    } finally {
      setIsDownloadingSitePrepReport({
        [report.jobId]: false
      });
    }
  };

  const [isDownloadingSitePlanningReport, setIsDownloadingSitePlanningReport] = useState<{
    [id: string | number]: boolean;
  }>({});
  const fetchSitePlanningReport = async (report: ISiteReport) => {
    try {
      setIsDownloadingSitePlanningReport({
        [report.jobId]: true
      });
      const res = await getSitePlanningReport(report.jobId);
      if (res && res.type.includes('application/pdf')) {
        return downloadFile(res, `Site Planning Report - ${report.code}`);
      }
    } catch (error) {
      enqueueSnackbar(
        error?.Detail || `Error downloading site planning reports, please try again.`,
        {
          variant: 'error'
        }
      );
    } finally {
      setIsDownloadingSitePlanningReport({
        [report.jobId]: false
      });
    }
  };

  useEffect(() => {
    if (selectedNeighborhood) {
      fetchSiteReports();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, perPage, filters]);

  const columns = useMemo(() => {
    return [
      {
        Header: 'Neighborhood',
        accessor: 'neighborhoodDescription',
        sort: false
      },
      {
        Header: 'Lot',
        accessor: 'lotNumber',
        sort: false
      },
      {
        Header: 'Job ID',
        accessor: 'code',
        sort: false
      },
      {
        Header: 'Status',
        accessor: 'jobStatus',
        sort: false
      },
      {
        Header: 'Type',
        accessor: 'jobType',
        sort: false
      },
      {
        id: 'actions',
        accessor: '',
        Header: '',
        sort: false,
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: ISiteReport } };
        }) => {
          return (
            <div className={classes.actionButtons}>
              <LoadingButton
                id={`site-prep-${original.jobId}`}
                loading={isDownloadingSitePrepReport[original.jobId]}
                loadingPosition="start"
                color="primary"
                title="Download Site Prep Report"
                startIcon={<FileDownload />}
                onClick={() => fetchSitePrepReport(original)}
              >
                {isMobile ? '' : 'Site '}Prep Report
              </LoadingButton>
              <LoadingButton
                id={`site-planning-${original.jobId}`}
                loading={isDownloadingSitePlanningReport[original.jobId]}
                loadingPosition="start"
                color="primary"
                title="Download Site Planning Report"
                startIcon={<FileDownload />}
                onClick={() => fetchSitePlanningReport(original)}
              >
                {isMobile ? '' : 'Site '}Planning Report
              </LoadingButton>
            </div>
          );
        }
      }
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobile, isDownloadingSitePrepReport, fetchSitePrepReport]);

  return (
    <OrderManagementPage title="Site Reports">
      <PageTitleWrapper>
        <PageTitle title="Site Reports" />
      </PageTitleWrapper>
      <SiteReportsFilters
        isLoading={isLoadingSiteReports}
        hasAppliedFilters={hasAppliedFilters}
        setHasAppliedFilters={setHasAppliedFilters}
        applyFilters={(clearFilters?: boolean, clearSearch?: boolean) => {
          if (clearFilters) {
            setSelectedNeighborhood(null);
            setFilters({
              jobStatus: null,
              jobType: null
            });
          } else {
            setFilters({
              ...filters,
              jobStatus: selectedStatus ?? undefined,
              jobType: selectedType ?? undefined
            });
          }
        }}
        selectedNeighborhood={selectedNeighborhood}
        setSelectedNeighborhood={setSelectedNeighborhood}
        selectedStatus={selectedStatus}
        setSelectedStatus={setSelectedStatus}
        selectedType={selectedType}
        setSelectedType={setSelectedType}
      />
      <Divider className={classes.divider} />
      {!hasAppliedFilters && !isLoadingSiteReports && (
        <Box my={1} justifyContent="center" display="flex">
          <Typography>Please select a Neighborhood to view associated site reports.</Typography>
        </Box>
      )}
      {!!selectedNeighborhood && hasAppliedFilters && (
        <Table
          columns={columns}
          data={siteReports}
          isLoading={isLoadingSiteReports}
          ResponsiveComponent={isMobile ? MobileSiteReports : undefined}
          mobileBreakPoint={1024}
          hidePagination
          mobileProps={{
            fetchSitePrepReport,
            isDownloadingSitePrepReport,
            fetchSitePlanningReport,
            isDownloadingSitePlanningReport
          }}
        />
      )}
      {!isLoadingSiteReports && selectedNeighborhood && hasAppliedFilters && (
        <Pagination
          page={page}
          count={recordCount}
          rowsPerPage={perPage}
          setPage={setPage}
          setRowsPerPage={setRowsPerPage}
        />
      )}
    </OrderManagementPage>
  );
};
const useStyles = makeStyles((theme: Theme) => ({
  divider: {
    margin: `${theme.spacing(1)} 0`
  },
  actionButtons: {
    textAlign: 'right'
  }
}));
